import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../Common_Files/Config';

/*
 * Proforma PDF Generator
 * 
 * Address overlap issue has been fixed by implementing:
 * 1. Text wrapping for long address lines using wrapText()
 * 2. Dynamic Y positioning based on actual wrapped line count
 * 3. Proper spacing calculation to prevent text overlap
 */
const generatePDF = async (id) => {
  try {
    const formData = new FormData();
    formData.set('sale_id', id);
    const response = await fetch(`${config.api_url}/ws-get-sales-by-id`, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();
    const products = Array.isArray(data.product) ? data.product : [];

    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 10;
    let y = margin;

    const wrapText = (text, maxWidth) => doc.splitTextToSize(text || '', maxWidth);

    const loadImageAsDataUrl = async (src) => {
      try {
        const res = await fetch(src, { cache: 'no-store' });
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        return null;
      }
    };

    const logoDataUrl = await loadImageAsDataUrl('/assets/img/logo.jpg');
    const logoWidth = 28;
    const logoHeight = 12;
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'JPEG', margin, y, logoWidth, logoHeight);
    }

    const leftX = margin + logoWidth + 5;
    doc.setFontSize(12).setFont('helvetica', 'bold');
    doc.text('Star Pacific UK LTD', leftX, y + 4);

    const companyAddress = [
      'Unit 6 Abenglen Industrial Estate,',
      'Betam Road, Hayes',
      'Middlesex, London UB3 1AT',
      'United Kingdom'
    ];
    let smallLineHeight = 4;
    doc.setFontSize(8).setFont('helvetica', 'normal');
    companyAddress.forEach((line, idx) => {
      doc.text(line, leftX, y + 4 + smallLineHeight * (idx + 1));
    });

    const regInfoStartX = leftX + 50;
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text('Co. Reg. No.: 7298396', regInfoStartX, y + 4);
    doc.text('VAT Reg. No.: GB994533184', regInfoStartX, y + 4 + smallLineHeight);
    doc.text('Tel: 44-208 329 3800', regInfoStartX, y + 4 + smallLineHeight * 2);

    const rightX = pageWidth - margin;
    doc.setFontSize(16).setFont('helvetica', 'bold');
    doc.setTextColor(255, 99, 71);
    doc.text('PRO FORMA', rightX, y + 4, { align: 'right' });
        doc.setTextColor(0, 0, 0);

    const formatDateDMY = (isoLike) => {
      if (!isoLike) return '';
      if (String(isoLike).startsWith('0000-00-00')) return '';
      try {
        const d = new Date(String(isoLike).replace(' ', 'T'));
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      } catch {
        const only = typeof isoLike === 'string' ? isoLike.split(' ')[0] : isoLike;
        const parts = String(only).split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return String(only || '');
      }
    };

    const formatMoney = (n) => {
      const num = isNaN(parseFloat(n)) ? 0 : parseFloat(n);
      return '£' + num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const addrStartY = y + 4 + (smallLineHeight * (companyAddress.length + 1)) + 5;
    const sale = data?.sale || {};

    const invoiceTitle = 'Invoice Address:';
    const invoiceLines = [
      sale.company_name || sale.billing_address_line1 || sale.address_line1 || '',
      sale.billing_address_line2 || sale.address_line2 || '',
      sale.billing_city || sale.city || '',
      sale.billing_postcode || sale.post_code || '',
      sale.billing_country || sale.country || ''
    ].filter(Boolean);

    // Invoice Address section
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text(invoiceTitle, margin, addrStartY);
    const invoiceValueStartX = margin + doc.getTextWidth(invoiceTitle + ' ');
    doc.setFontSize(8).setFont('helvetica', 'normal');
    
    let invoiceY = addrStartY;
    let totalInvoiceLines = 0;
    if (invoiceLines.length > 0) {
      invoiceLines.forEach((line, idx) => {
        const wrappedLines = wrapText(line, 60);
        wrappedLines.forEach((wrappedLine, lineIdx) => {
          doc.text(wrappedLine, invoiceValueStartX, invoiceY);
          invoiceY += smallLineHeight;
          totalInvoiceLines++;
        });
      });
    }

    const secondRowY = addrStartY - 20 + (totalInvoiceLines * smallLineHeight) + 5;

    const sectionWidth = (pageWidth - margin * 2) / 3;

    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text(`VAT Reg. No.: ${sale.customer_vat || ''}`, margin + 60, secondRowY);
    doc.text(`Rep: ${sale.rep || ''}`, margin + 60, secondRowY + 4);
    doc.text(`Account No: ${sale.account_no || ''}`, margin + 60, secondRowY + 8);

    doc.text(`Job Ref: ${sale.job_ref || ''}`, margin + sectionWidth + 30, secondRowY);
    //doc.text(`Payment Terms: 14 Days from Date of Invoice`, margin + sectionWidth + 30, secondRowY + 4);

    doc.text(`Currency: GBP`, margin + sectionWidth * 2.5, secondRowY);
    doc.text(`Customer Ref: ${sale.customer_ref || ''}`, margin + sectionWidth * 2.5, secondRowY + 4);
    doc.text(`Booking Ref: ${sale.booking_ref || ''}`, margin + sectionWidth * 2.5, secondRowY + 8);
    // doc.text(`Date: ${formatDateDMY(sale.date || new Date())}`, margin + sectionWidth * 2.5, secondRowY + 12);
    doc.text(`Delivery Date: ${formatDateDMY(sale.delivery_date)}`, margin + sectionWidth * 2.5, secondRowY + 12);
    doc.text(`SO#: ${sale.reference_no || ''}`, margin + sectionWidth * 2.5, secondRowY + 16);

    const thirdRowY = secondRowY + 20;

    const deliveryTitle = 'Delivery Address:';
    const deliveryLines = [
      sale.address_line1 || '',
      sale.address_line2 || '',
      sale.city || '',
      sale.post_code || '',
      sale.country || ''
    ].filter(Boolean);

    // Delivery Address section
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text(deliveryTitle, margin, thirdRowY);
    const deliveryValueStartX = margin + doc.getTextWidth(deliveryTitle + ' ');
    doc.setFontSize(8).setFont('helvetica', 'normal');
    
    let deliveryY = thirdRowY;
    let totalDeliveryLines = 0;
    if (deliveryLines.length > 0) {
      deliveryLines.forEach((line, idx) => {
        const wrappedLines = wrapText(line, 60);
        wrappedLines.forEach((wrappedLine, lineIdx) => {
          doc.text(wrappedLine, deliveryValueStartX, deliveryY);
          deliveryY += smallLineHeight;
          totalDeliveryLines++;
        });
      });
    }

    y = thirdRowY + (totalDeliveryLines * smallLineHeight) + 10;

    const columns = [
      'Line No', 'SKU Code', 'Product Description', 'Pack', 'Size', 'Unit Barcode', 'Case Qty', 'Unit Price', 'Amount', 'VAT Rate(%)', 'VAT Amount', 'Total Amount'
    ];

    const body = products.map((item, index) => {
      const qty = parseFloat(item.salesQty || item.quantity || 0) || 0;
      const unitPrice = parseFloat(item.price || 0) || 0;
      const net = qty * unitPrice;
      const vatRate = (item.vat != null) ? parseFloat(item.vat) : ((parseFloat(item.item_tax || 0) / (unitPrice || 1)) * 100 || 0);
      const vatAmt = net * (isNaN(vatRate) ? 0 : (vatRate / 100));
      const total = net + vatAmt;
      return [
        index + 1,
        item.code || '',
        wrapText(item.product_desc || '', 50).join('\n'),
        item.pack || '',
        item.size || '',
        item.unit_barcode || item.unit_brcode || '',
        qty,
        formatMoney(unitPrice),
        formatMoney(net),
        (isNaN(vatRate) ? '0.00' : vatRate.toFixed(2)),
        formatMoney(vatAmt),
        formatMoney(total),
      ];
    });

    const totalQty = body.reduce((s, r) => s + parseFloat(r[6] || 0), 0);
    const totalNet = products.reduce((s, item) => {
      const qty = parseFloat(item.salesQty || item.quantity || 0) || 0;
      const unitPrice = parseFloat(item.price || 0) || 0;
      return s + qty * unitPrice;
    }, 0);
    const totalVat = products.reduce((s, item) => {
      const qty = parseFloat(item.salesQty || item.quantity || 0) || 0;
      const unitPrice = parseFloat(item.price || 0) || 0;
      const net = qty * unitPrice;
      const vatRate = (item.vat != null) ? parseFloat(item.vat) : ((parseFloat(item.item_tax || 0) / (unitPrice || 1)) * 100 || 0);
      return s + net * (isNaN(vatRate) ? 0 : (vatRate / 100));
    }, 0);
    const totalAmt = totalNet + totalVat;
    body.push(['', '', 'Total:', '', '', '', totalQty.toFixed(0), '', formatMoney(totalNet), '', formatMoney(totalVat), formatMoney(totalAmt)]);

    doc.autoTable({
      startY: y,
      head: [columns],
      body,
      theme: 'grid',
      headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000', lineStyle: 'solid' },
      bodyStyles: { fontSize: 7, lineHeight: 1.5, lineColor: '#000', lineWidth: 0.09 },
      margin: { top: 10 }
    });

    y = doc.autoTable.previous.finalY + 10;

    const footerSectionWidth = (pageWidth - margin * 2) / 3;

    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text(`Notes: ${sale.notes || ''}`, margin, y);
    doc.text(`Ref: ${sale.job_ref || sale.refno || ''}`, margin, y + 5);

    doc.text(`Gross Weight: ${sale.gross_weight || '0.00'} KGS`, margin + footerSectionWidth * 2, y);
    doc.text(`CBM: ${sale.cube || '0.00'} M3`, margin + footerSectionWidth * 2, y + 5);
    doc.text(`Net Weight: ${sale.net_weight || '0.00'} KGS`, margin + footerSectionWidth * 2, y + 10);

    const bottomY = pageHeight - margin - 20;
    const centerX = pageWidth / 2;
    const footerTexts = [
      'Subject to Star Pacific Limited terms and conditions of sale, a copy of which is available on request',
      'Star Pacific UK LTD (No. 7298396)',
      'Reservation of Title. The property in and the title to all goods sold by Star Pacific UK LTD shall not pass to the buyer until all monies due and owing have been paid in full'
    ];

    footerTexts.forEach((text, i) => {
      wrapText(text, pageWidth - 20).forEach((line, lineIdx) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(line, centerX, bottomY + (i + lineIdx) * 5, { align: 'center' });
      });
    });

    // Add page numbering at bottom right
    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text('Page 1 of 1', pageWidth - margin, pageHeight - margin, { align: 'right' });

    const blobUrl = doc.output('bloburl');
    window.open(blobUrl, '_blank');
  } catch (error) {
    console.error('Error generating Pro Forma PDF:', error);
  }
};

export default generatePDF;

// Helper to generate the same Proforma PDF but return as a Blob and filename for uploading
export const generateProformaPDFBlob = async (id) => {
  try {
    const formData = new FormData();
    formData.set('sale_id', id);
    const response = await fetch(`${config.api_url}/ws-get-sales-by-id`, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();
    const products = Array.isArray(data.product) ? data.product : [];

    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 10;
    let y = margin;

    const wrapText = (text, maxWidth) => doc.splitTextToSize(text || '', maxWidth);

    const loadImageAsDataUrl = async (src) => {
      try {
        const res = await fetch(src, { cache: 'no-store' });
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        return null;
      }
    };

    const logoDataUrl = await loadImageAsDataUrl('/assets/img/logo.jpg');
    const logoWidth = 28;
    const logoHeight = 12;
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'JPEG', margin, y, logoWidth, logoHeight);
    }

    const leftX = margin + logoWidth + 5;
    doc.setFontSize(12).setFont('helvetica', 'bold');
    doc.text('Star Pacific UK LTD', leftX, y + 4);

    const companyAddress = [
      'Unit 6 Abenglen Industrial Estate,',
      'Betam Road, Hayes',
      'Middlesex, London UB3 1AT',
      'United Kingdom'
    ];
    let smallLineHeight = 4;
    doc.setFontSize(8).setFont('helvetica', 'normal');
    companyAddress.forEach((line, idx) => {
      doc.text(line, leftX, y + 4 + smallLineHeight * (idx + 1));
    });

    const regInfoStartX = leftX + 50;
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text('Co. Reg. No.: 7298396', regInfoStartX, y + 4);
    doc.text('VAT Reg. No.: GB994533184', regInfoStartX, y + 4 + smallLineHeight);
    doc.text('Tel: 44-208 329 3800', regInfoStartX, y + 4 + smallLineHeight * 2);

    const rightX = pageWidth - margin;
    doc.setFontSize(16).setFont('helvetica', 'bold');
    doc.setTextColor(255, 99, 71);
    doc.text('PRO FORMA', rightX, y + 4, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    const formatDateDMY = (isoLike) => {
      if (!isoLike) return '';
      if (String(isoLike).startsWith('0000-00-00')) return '';
      try {
        const d = new Date(String(isoLike).replace(' ', 'T'));
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      } catch {
        const only = typeof isoLike === 'string' ? isoLike.split(' ')[0] : isoLike;
        const parts = String(only).split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return String(only || '');
      }
    };

    const formatMoney = (n) => {
      const num = isNaN(parseFloat(n)) ? 0 : parseFloat(n);
      return '£' + num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const addrStartY = y + 4 + (smallLineHeight * (companyAddress.length + 1)) + 5;
    const sale = data?.sale || {};

    const invoiceTitle = 'Invoice Address:';
    const invoiceLines = [
      sale.company_name || sale.billing_address_line1 || sale.address_line1 || '',
      sale.billing_address_line2 || sale.address_line2 || '',
      sale.billing_city || sale.city || '',
      sale.billing_postcode || sale.post_code || '',
      sale.billing_country || sale.country || ''
    ].filter(Boolean);

    // Invoice Address section
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text(invoiceTitle, margin, addrStartY);
    const invoiceValueStartX = margin + doc.getTextWidth(invoiceTitle + ' ');
    doc.setFontSize(8).setFont('helvetica', 'normal');
    
    let invoiceY = addrStartY;
    let totalInvoiceLines = 0;
    if (invoiceLines.length > 0) {
      invoiceLines.forEach((line, idx) => {
        const wrappedLines = wrapText(line, 60);
        wrappedLines.forEach((wrappedLine, lineIdx) => {
          doc.text(wrappedLine, invoiceValueStartX, invoiceY);
          invoiceY += smallLineHeight;
          totalInvoiceLines++;
        });
      });
    }

    const secondRowY = addrStartY - 20 + (totalInvoiceLines * smallLineHeight) + 5;

    const sectionWidth = (pageWidth - margin * 2) / 3;

    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text(`VAT Reg. No.: ${sale.customer_vat || ''}`, margin + 60, secondRowY);
    doc.text(`Rep: ${sale.rep || ''}`, margin + 60, secondRowY + 4);
    doc.text(`Account No: ${sale.account_no || ''}`, margin + 60, secondRowY + 8);

    doc.text(`Job Ref: ${sale.job_ref || ''}`, margin + sectionWidth + 30, secondRowY);
    doc.text(`Payment Terms: 14 Days from Date of Invoice`, margin + sectionWidth + 30, secondRowY + 4);

    doc.text(`Currency: GBP`, margin + sectionWidth * 2.5, secondRowY);
    doc.text(`Customer Ref: ${sale.customer_ref || ''}`, margin + sectionWidth * 2.5, secondRowY + 4);
    doc.text(`Booking Ref: ${sale.booking_ref || ''}`, margin + sectionWidth * 2.5, secondRowY + 8);
    doc.text(`Delivery Date: ${formatDateDMY(sale.delivery_date)}`, margin + sectionWidth * 2.5, secondRowY + 12);
    doc.text(`SO#: ${sale.reference_no || ''}`, margin + sectionWidth * 2.5, secondRowY + 16);

    const thirdRowY = secondRowY + 20;

    const deliveryTitle = 'Delivery Address:';
    const deliveryLines = [
      sale.address_line1 || '',
      sale.address_line2 || '',
      sale.city || '',
      sale.post_code || '',
      sale.country || ''
    ].filter(Boolean);

    // Delivery Address section
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text(deliveryTitle, margin, thirdRowY);
    const deliveryValueStartX = margin + doc.getTextWidth(deliveryTitle + ' ');
    doc.setFontSize(8).setFont('helvetica', 'normal');
    
    let deliveryY = thirdRowY;
    let totalDeliveryLines = 0;
    if (deliveryLines.length > 0) {
      deliveryLines.forEach((line, idx) => {
        const wrappedLines = wrapText(line, 60);
        wrappedLines.forEach((wrappedLine, lineIdx) => {
          doc.text(wrappedLine, deliveryValueStartX, deliveryY);
          deliveryY += smallLineHeight;
          totalDeliveryLines++;
        });
      });
    }

    y = thirdRowY + (totalDeliveryLines * smallLineHeight) + 10;

    const columns = [
      'Line No', 'SKU Code', 'Product Description', 'Pack', 'Size', 'Unit Barcode', 'Case Qty', 'Unit Price', 'Amount', 'VAT Rate(%)', 'VAT Amount', 'Total Amount'
    ];

    const body = products.map((item, index) => {
      const qty = parseFloat(item.salesQty || item.quantity || 0) || 0;
      const unitPrice = parseFloat(item.price || 0) || 0;
      const net = qty * unitPrice;
      const vatRate = (item.vat != null) ? parseFloat(item.vat) : ((parseFloat(item.item_tax || 0) / (unitPrice || 1)) * 100 || 0);
      const vatAmt = net * (isNaN(vatRate) ? 0 : (vatRate / 100));
      const total = net + vatAmt;
      return [
        index + 1,
        item.code || '',
        wrapText(item.product_desc || '', 50).join('\n'),
        item.pack || '',
        item.size || '',
        item.unit_barcode || item.unit_brcode || '',
        qty,
        formatMoney(unitPrice),
        formatMoney(net),
        (isNaN(vatRate) ? '0.00' : vatRate.toFixed(2)),
        formatMoney(vatAmt),
        formatMoney(total),
      ];
    });

    const totalQty = body.reduce((s, r) => s + parseFloat(r[6] || 0), 0);
    const totalNet = products.reduce((s, item) => {
      const qty = parseFloat(item.salesQty || item.quantity || 0) || 0;
      const unitPrice = parseFloat(item.price || 0) || 0;
      return s + qty * unitPrice;
    }, 0);
    const totalVat = products.reduce((s, item) => {
      const qty = parseFloat(item.salesQty || item.quantity || 0) || 0;
      const unitPrice = parseFloat(item.price || 0) || 0;
      const net = qty * unitPrice;
      const vatRate = (item.vat != null) ? parseFloat(item.vat) : ((parseFloat(item.item_tax || 0) / (unitPrice || 1)) * 100 || 0);
      return s + net * (isNaN(vatRate) ? 0 : (vatRate / 100));
    }, 0);
    const totalAmt = totalNet + totalVat;
    body.push(['', '', 'Total:', '', '', '', totalQty.toFixed(0), '', formatMoney(totalNet), '', formatMoney(totalVat), formatMoney(totalAmt)]);

    doc.autoTable({
      startY: y,
      head: [columns],
      body,
      theme: 'grid',
      headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000', lineStyle: 'solid' },
      bodyStyles: { fontSize: 7, lineHeight: 1.5, lineColor: '#000', lineWidth: 0.09 },
      margin: { top: 10 }
    });

    y = doc.autoTable.previous.finalY + 10;

    const footerSectionWidth = (pageWidth - margin * 2) / 3;

    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text(`Notes: ${sale.notes || ''}`, margin, y);
    doc.text(`Ref: ${sale.job_ref || sale.refno || ''}`, margin, y + 5);

    doc.text(`Gross Weight: ${sale.gross_weight || '0.00'} KGS`, margin + footerSectionWidth * 2, y);
    doc.text(`CBM: ${sale.cube || '0.00'} M3`, margin + footerSectionWidth * 2, y + 5);
    doc.text(`Net Weight: ${sale.net_weight || '0.00'} KGS`, margin + footerSectionWidth * 2, y + 10);

    const bottomY = pageHeight - margin - 20;
    const centerX = pageWidth / 2;
    const footerTexts = [
      'Subject to Star Pacific Limited terms and conditions of sale, a copy of which is available on request',
      'Star Pacific UK LTD (No. 7298396)',
      'Reservation of Title. The property in and the title to all goods sold by Star Pacific UK LTD shall not pass to the buyer until all monies due and owing have been paid in full'
    ];

         footerTexts.forEach((text, i) => {
       wrapText(text, pageWidth - 20).forEach((line, lineIdx) => {
         doc.setFontSize(8);
         doc.setFont('helvetica', 'normal');
         doc.text(line, centerX, bottomY + (i + lineIdx) * 5, { align: 'center' });
       });
     });

     // Add page numbering at bottom right
     doc.setFontSize(8).setFont('helvetica', 'normal');
     doc.text('Page 1 of 1', pageWidth - margin, pageHeight - margin, { align: 'right' });

     const uniqueFilename = generateUniqueFilename('Proforma', 'pdf');
     const blob = doc.output('blob');
     return { blob, filename: uniqueFilename };
  } catch (error) {
    console.error('Error generating Pro Forma PDF:', error);
    return null;
  }
};

// Helper function to generate unique filename
const generateUniqueFilename = (baseName, extension) => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
  return `${baseName}_${timestamp}.${extension}`;
};
